// Libraries
import React, { Fragment, useEffect, useState } from 'react';
import Modal from 'react-modal';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import { Button, Col, Form, Row } from 'react-bootstrap';
// Services
import { getBridgePayers } from '../../../services/administration';
import { checkEligibility, getPayerInfo } from '../../../services/eligibilityChecker';
// Actions
import ShowNotification from '../../../actions/notification';
import { UpdatePatient } from '../../../actions/patient';
// Constants
import { DIALOG_STYLES, NOTIFICATION_TYPE, US_STATES } from '../../../constants/constants';
import { ELIGIBILITY_CHECK_SCHEMA } from '../../../constants/forms/schemas/modalsSchemas';
// Components
import { Datepicker } from '../../base/forms/Datepicker';
import { TextInput } from '../../base/forms/TextInput';
import { Select } from '../../base/forms/Select';
import InsuranceEligibility from './InsuranceEligibility';
import { Loading } from '../../base/Loading';

export function EligibilityCheckModal(props) {
  const {
    isModalOpen, setIsModalOpen, patientId,
  } = props;

  const dispatch = useDispatch();
  const showNotification = data => dispatch(ShowNotification(data));
  const updatePatient = data => dispatch(UpdatePatient(data));

  const [bridgePayers, setBridgePayers] = useState([]);
  const [isEligibilityResults, setIsEligibilityResults] = useState(false);
  const [isEligibilityInProgress, setIsEligibilityInProgress] = useState(false);
  const [validationSchema, setValidationSchema] = useState(ELIGIBILITY_CHECK_SCHEMA([]));
  const [isHintLoading, setIsHintLoading] = useState(false);
  const [pendingEligibilityForm, setPendingEligibilityForm] = useState(null);

  const patient = useSelector(state => state.patient || {});

  const getEligibilityInProgress = () => {
    const inProgress = patient?.insuranceEligibility && patient?.insuranceEligibility?.some(
      service => service?.status?.toLowerCase() === 'pending',
    ) || false;
    setIsEligibilityInProgress(inProgress);
    return inProgress;
  };
  const getPayer = (name) => bridgePayers.find(bridgePayer => bridgePayer.name === name);

  const ValidationTrigger = ({ validateForm }) => {
    useEffect(() => {
      validateForm();
    }, [validateForm]);
    return null;
  };

  function getDateOfBirth(dateToFormat) {
    let dateOfBirth = null;

    if (dateToFormat) {
      if (dateToFormat instanceof Date) {
        const isoDate = dateToFormat.toISOString();
        dateToFormat = isoDate.split('T')[0];
      }

      if (dateToFormat.includes('-')) {
        // If DOB has hyphens, assume yyyy-MM-dd and format as MM/dd/yyyy
        const [year, month, day] = dateToFormat.split('-');
        dateOfBirth = `${month}/${day}/${year}`;
      } else if (dateToFormat.includes('/')) {
        // if DOB has slashes, assume MM/dd/yyyy and format as yyyy-MM-dd
        const [month, day, year] = dateToFormat.split('/');
        dateOfBirth = `${year}-${month}-${day}`;
      }
    }

    return dateOfBirth;
  }

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleCarrierChange = (formik, carrier) => {
    // Set the selected values
    formik.setFieldValue('insuranceCarrierId', carrier ? carrier.payer_id : null);
    formik.setFieldValue('insuranceCarrier', carrier ? carrier.name : null);

    // Clear Member ID when carrier changes
    formik.setFieldValue('memberId', '');
    formik.setFieldTouched('memberId', true, true);

    if (carrier?.payer_id) {
      // Clear previous messages
      updatePatient({
        eligibilityForm: {
          ...patient?.eligibilityForm,
          hints: null,
          errors: null,
          bridgePolicyErrors: null,
        },
      });
      // Get the payer data for hints
      setIsHintLoading(true);
      getPayerInfoData(carrier.payer_id);
    }
  };

  const submitEligibilityCheck = (eligibilityForm) => {
    // Set in-progress first so React renders the spinner before switching to results view
    setIsEligibilityInProgress(true);
    setPendingEligibilityForm(eligibilityForm);
  };

  const getPayerInfoData = (payerId) => {
    // Call Get Payer Info
    setIsHintLoading(true);
    const getPayerInfoPromise = getPayerInfo(patientId, payerId).promise;

    getPayerInfoPromise.then((data) => {
      delete getPayerInfoPromise.promise;

      // Update bridgePayer info
      const payerData = data ?? {};
      if (payerData?.id) {
        updateBridgePayer(payerData);
      }
    })
    .catch(() => {
      // Ignore errors given and show no hint data
      delete getPayerInfoPromise.promise;
    })
    .finally(() => {
      setIsHintLoading(false);
    });
  };

  const getSelectedPayerHint = (carrierId) => {
    if (patient?.eligibilityForm?.hints) return patient.eligibilityForm.hints;
    if (!carrierId) return null;
    const payer = bridgePayers.find(bp => bp.payer_id === carrierId);
    return payer?.hints || null;
  };

  const updateBridgePayer = (payer) => {
    const updatedBridgePayers = bridgePayers.map(bPayer =>
    bPayer.payer_id === payer?.id ? {
      ...bPayer,
      hints: payer.hint
    } : bPayer);
    setBridgePayers(updatedBridgePayers);
  };

  const getModalTitle = () => (
    <div className="dialog-title" data-test="checkEligibilityModal_title">
      { isEligibilityResults ? (
        isEligibilityInProgress ?
          "Checking Eligibility" : "Eligibility Results"
        ) : ("Check Eligibility")
      }
      <button
        type="button"
        className="close-icon i-close"
        onClick={() => handleCloseModal()}
        data-test="checkEligibilityModal_closeBtn"
      />
    </div>
  );

  useEffect(() => {
    if (patient && !patient.eligibilityForm && bridgePayers.length > 0) {
      // Default form to patient data
      const payer = getPayer(patient.primaryInsurance?.companyName);
      updatePatient({
        eligibilityForm: {
          firstName: patient.firstName,
          lastName: patient.lastName,
          dateOfBirth: getDateOfBirth(patient.dateOfBirth),
          state: patient.addressInfo?.state,
          insuranceCarrier: patient.primaryInsurance?.companyName,
          insuranceCarrierId: payer?.payer_id,
          memberId: patient.primaryInsurance?.planId,
          hints: payer?.hint,
        },
      });

      if (payer?.payer_id) {
        getPayerInfoData(payer.payer_id);
      }
    }
  }, [patient, bridgePayers]);

  useEffect(() => {
    // Check if there is a previous insuranceEligibility to populate the form with
    if (patient.insuranceEligibility
      && patient.insuranceEligibility.length > 0
      // Use updatedAt to determine if this is the default or an existing one from the DB
      && patient.insuranceEligibility[0].updatedAt) {

      const foundEligibility = patient.insuranceEligibility[0];
      const combinedHints = [...new Set(patient.insuranceEligibility
        .map(insuranceEligibility => insuranceEligibility.hints)
        .filter(Boolean))]
        .join(", ");
      const combinedErrors = [...new Set(patient.insuranceEligibility
        .map(insuranceEligibility => insuranceEligibility.errors)
        .filter(Boolean))]
        .join(", ");
      const combinedBridgePolicyErrors = [...new Set(patient.insuranceEligibility
        .flatMap(insuranceEligibility => insuranceEligibility.bridgePolicyErrors)
        .map(bridgeError => bridgeError?.message)
        .filter(Boolean))];

      updatePatient({
        eligibilityForm: {
          ...foundEligibility,
          dateOfBirth: getDateOfBirth(foundEligibility.dateOfBirth),
          insuranceCarrier: foundEligibility.payerName,
          insuranceCarrierId: foundEligibility.payerId,
          hints: combinedHints,
          errors: combinedErrors,
          bridgePolicyErrors: combinedBridgePolicyErrors,
        },
      });

      // Check if eligibility is in progress
      const inProgress = getEligibilityInProgress();
      if (!isEligibilityResults) {
        setIsEligibilityResults(inProgress);
      } else if (combinedErrors.length > 0 || combinedBridgePolicyErrors.length > 0) {
        setIsEligibilityResults(false);
      }
    }
  }, [patient.insuranceEligibility]);

  useEffect(() => {
    setIsEligibilityResults(getEligibilityInProgress());
  }, [isModalOpen]);

  useEffect(() => {
    if (!pendingEligibilityForm) return;

    const eligibilityForm = { ...pendingEligibilityForm };
    setPendingEligibilityForm(null);

    // Ensure eligibilityForm date of birth does not have a date object for serialization
    if (eligibilityForm?.dateOfBirth && eligibilityForm.dateOfBirth instanceof Date) {
      eligibilityForm.dateOfBirth = getDateOfBirth(eligibilityForm.dateOfBirth);
    }

    // Set the services to pending and update with form data
    const insuranceEligibility = patient?.insuranceEligibility?.map((eligibility) => {
      return {
        serviceType: eligibility.serviceType,
        firstName: eligibilityForm.firstName,
        lastName: eligibilityForm.lastName,
        dateOfBirth: getDateOfBirth(eligibilityForm.dateOfBirth),
        state: eligibilityForm.state,
        memberId: eligibilityForm.memberId,
        payerName: eligibilityForm.insuranceCarrier,
        payerId: eligibilityForm.insuranceCarrierId,
        status: 'PENDING',
      };
    });

    // Save the state of the form
    updatePatient({ eligibilityForm, insuranceEligibility });
    setIsEligibilityResults(true);

    // Call Eligibility Check
    const checkEligibilityPromise = checkEligibility(patientId, insuranceEligibility).promise;

    checkEligibilityPromise.then((data) => {
        delete checkEligibilityPromise.promise;

        // Update both the eligibility requests and the patient's primary insurance locally
        updatePatient({
          insuranceEligibility: data?.recentEligibilityRequests,
          primaryInsurance: {
            ...patient?.primaryInsurance,
            companyName: eligibilityForm.insuranceCarrier,
            planId: eligibilityForm.memberId,
          }
        });
      })
      .catch((error) => {
        delete checkEligibilityPromise.promise;
        if (error.isCanceled || error.status === 401 || error.status === 403) {
          return;
        }

        showNotification({
          message: 'An error occurred while attempting to check eligibility.',
          autoHide: true,
          notificationType: NOTIFICATION_TYPE.ERROR,
        });
    });
  }, [pendingEligibilityForm]);

  useEffect(() => {
    // Prevent reload if eligibility is in progress, and we're not on the form or final results
    if (isEligibilityInProgress && isEligibilityResults) {
      const handleBeforeUnload = (event) => {
        event.preventDefault();
        // For legacy browsers
        // eslint-disable-next-line no-param-reassign
        event.returnValue = true;
      };

      window.addEventListener('beforeunload', handleBeforeUnload);

      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }
  }, [isEligibilityInProgress, isEligibilityResults]);

  useEffect(() => {
    if (bridgePayers && bridgePayers.length === 0) {
      const getBridgePayersPromise = getBridgePayers().promise;

      getBridgePayersPromise
        .then((data) => {
          delete getBridgePayersPromise.promise;
          // [ { name: 'Test Insurance', payer_id: 'pyr_abc123' } ]
          const bridgePayers = JSON.parse(data?.value?.value) ?? [];
          setBridgePayers(bridgePayers);

          // Set the form schema when bridge payers are updated
          setValidationSchema(ELIGIBILITY_CHECK_SCHEMA(bridgePayers));
        })
        .catch((error) => {
          delete getBridgePayersPromise.promise;
          if (error.isCanceled || error.status === 401 || error.status === 403) {
            return;
          }
        });
    }
  }, []);


  return (
    <div>
      <Modal
        isOpen={isModalOpen}
        style={DIALOG_STYLES}
        onRequestClose={() => handleCloseModal()}
        contentLabel="Check Eligibility"
        data-test="checkEligibilityModal"
        shouldCloseOnOverlayClick={false}
      >
        {isEligibilityResults ? (
          <div className="simple-dialog medium-dialog">
            {getModalTitle()}
            <div className="dialog-content text-left">
              {isEligibilityInProgress ? (
                <Fragment>
                  <div className="dialog-loading">
                    <Loading data-test="checkEligibilityModal_loading" forceLoading />
                    <div className="dialog-loading-text">Please wait...</div>
                  </div>
                </Fragment>
              ) : (
                <InsuranceEligibility
                  data-test="checkEligibilityModal_patientEligibility"
                  services={patient?.insuranceEligibility}
                  modalFormat={true}
                />
              )}
            </div>
            <div className="dialog-buttons justify-content-between px-4">
              <Button
                variant="light"
                onClick={() => setIsEligibilityResults(false)}
                data-test="checkEligibilityModal_backBtn"
              >
                Back
              </Button>
              <Button
                variant="primary"
                className="ml-2"
                onClick={() => handleCloseModal()}
                disabled={isEligibilityInProgress}
                data-test="checkEligibilityModal_okBtn"
              >
                OK
              </Button>
            </div>
          </div>
        ) : (
          <div className="simple-dialog medium-dialog">
            {getModalTitle()}
            <Formik
              initialValues={patient?.eligibilityForm}
              validationSchema={validationSchema}
              onSubmit={(values) => submitEligibilityCheck(values)}
              data-test="checkEligibilityModal_formikComponent"
            >
              {formik => (
                <Form>
                  <ValidationTrigger validateForm={formik.validateForm} />
                  <div className="dialog-content text-left eligibility-check-form">
                    <Row>
                      <Col className="col-md-6 form-label-adjust" data-test="checkEligibilityModal_firstNameInput">
                        <TextInput
                          autoComplete="off"
                          name="firstName"
                          label="First Name"
                          data-test="checkEligibilityModal_firstNameInputText"
                          styles={{ formInvalid: 'bi-exclamation-circle-fill', formLabel: 'float-left' }}
                        />
                      </Col>
                      <Col className="col-md-6 form-label-adjust" data-test="checkEligibilityModal_lastNameInput">
                        <TextInput
                          autoComplete="off"
                          name="lastName"
                          label="Last Name"
                          data-test="checkEligibilityModal_lastNameInputText"
                          styles={{ formInvalid: 'bi-exclamation-circle-fill', formLabel: 'float-left' }}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col className="col-md-6 form-label-adjust" data-test="checkEligibilityModal_dateOfBirthInput">
                        <Datepicker
                          label="Date of Birth"
                          name="dateOfBirth"
                          dateFormat="MM/dd/yyyy"
                          autoComplete="off"
                          data-test="checkEligibilityModal_dateOfBirthInputText"
                          popperProps={{
                            strategy: 'fixed',
                          }}
                          styles={{ formInvalid: 'bi-exclamation-circle-fill' }}
                        />
                      </Col>
                      <Col className="col-md-6" data-test="checkEligibilityModal_stateInput">
                        <Select
                          label="State"
                          name="state"
                          placeholder="Select..."
                          noResultsText="No results found"
                          options={US_STATES}
                          searchable
                          data-test="checkEligibilityModal_stateSelect"
                          styles={{ formInvalid: 'bi-exclamation-circle-fill' }}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col className="col-md-6" data-test="checkEligibilityModal_insuranceCarrierInput">
                        <Select
                          label="Insurance Carrier"
                          name="insuranceCarrier"
                          placeholder="Select..."
                          noResultsText="No results found"
                          valueKey="name"
                          labelKey="name"
                          options={bridgePayers}
                          searchable
                          onChange={(value) => handleCarrierChange(formik, value)}
                          data-test="checkEligibilityModal_insuranceCarrierSelect"
                          styles={{ formInvalid: 'bi-exclamation-circle-fill' }}
                          menuStyle={{ maxHeight: '120px' }}
                          disabled={isHintLoading}
                        />
                      </Col>
                      <Col className="col-md-6 form-label-adjust" data-test="checkEligibilityModal_memberIdInput">
                        <TextInput
                          autoComplete="off"
                          name="memberId"
                          label="Member ID"
                          data-test="checkEligibilityModal_memberIdInputText"
                          styles={{ formInvalid: 'bi-exclamation-circle-fill', formLabel: 'float-left' }}
                        />
                      </Col>
                    </Row>
                  </div>
                  <div className="dialog-content">
                    {!isHintLoading && getSelectedPayerHint(formik.values?.insuranceCarrierId) && (
                    <div data-test="checkEligibilityModal_hints" className="text-ccm-blue text-left">
                      <i className="bi-info-circle-fill pr-2" />Hints: {getSelectedPayerHint(formik.values?.insuranceCarrierId)}
                    </div>
                    )}
                    {patient?.eligibilityForm?.errors && (
                    <div data-test="checkEligibilityModal_errors" className="text-ccm-red text-left">
                      <i className="bi-info-circle-fill pr-2" />Errors: {patient?.eligibilityForm?.errors}
                    </div>
                    )}
                    {patient?.eligibilityForm?.bridgePolicyErrors && patient?.eligibilityForm?.bridgePolicyErrors?.length > 0 && (
                    <div data-test="checkEligibilityModal_bridgePolicyErrors" className="text-ccm-red text-left">
                      {patient?.eligibilityForm?.bridgePolicyErrors?.map((policyError, index) => (
                      <div key={`policyError_${index}`}>
                        <i className="bi-info-circle-fill pr-2" />Policy Error: {policyError}
                      </div>
                      ))}
                    </div>
                    )}
                  </div>
                  <div className="dialog-buttons justify-content-end px-4">
                    <Button
                      variant="light"
                      onClick={() => handleCloseModal()}
                      data-test="checkEligibilityModal_cancelBtn"
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      className="ml-2"
                      onClick={() => formik.handleSubmit()}
                      disabled={!formik.isValid || isHintLoading}
                      data-test="checkEligibilityModal_submitBtn"
                    >
                      Submit
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        )}
      </Modal>
    </div>
  );
}
